/**
 * Hover-with-grace-period open/close state for a floating preview (APP-3397). The trigger opens on
 * pointer-enter; leaving the trigger OR the preview schedules a delayed close that a re-enter cancels, so the
 * pointer can travel the gap between the two without the preview flickering shut. Focus inside the preview
 * also holds it open (blur re-checks). Open is pointer-only — matching the prior dock behaviour — so focusing
 * the trigger after a collapse does not surprise-open the preview.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import type { FocusEvent } from "react"

export const PREVIEW_CLOSE_DELAY_MS = 150

export function useHoverPreview(closeDelayMs: number = PREVIEW_CLOSE_DELAY_MS) {
  const [isOpen, setIsOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  // Whether the pointer is over the trigger or preview — a "keep open" signal the delayed close honours.
  const pointerOverRef = useRef(false)
  const surfaceRef = useRef<HTMLDivElement>(null)

  useEffect(() => () => clearTimeout(closeTimer.current), [])

  const open = useCallback(() => {
    pointerOverRef.current = true
    clearTimeout(closeTimer.current)
    setIsOpen(true)
  }, [])

  const closeNow = useCallback(() => {
    clearTimeout(closeTimer.current)
    // A row click / new-chat tears the Popper out from under the cursor with no mouseleave, so reset the
    // pointer signal too — left stale-true, a later scheduleClose would wrongly bail and strand it open.
    pointerOverRef.current = false
    setIsOpen(false)
  }, [])

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => {
      // Each sticky signal (pointer, focus) re-schedules a close when it clears, so bailing never strands it open.
      if (pointerOverRef.current) return
      if (surfaceRef.current?.contains(document.activeElement)) return
      setIsOpen(false)
    }, closeDelayMs)
  }, [closeDelayMs])

  const handlePointerLeave = useCallback(() => {
    pointerOverRef.current = false
    scheduleClose()
  }, [scheduleClose])

  // Focus leaving the preview subtree is a cleared sticky signal — re-check whether it should close.
  const handleSurfaceBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      // A null relatedTarget means focus went nowhere — still a real focus-out. Without scheduling
      // here, focus-held-then-null-blur strands the preview open. The timer's pointer-over check
      // prevents a spurious close when the pointer remains over the trigger or surface.
      if (!surfaceRef.current?.contains(event.relatedTarget)) scheduleClose()
    },
    [scheduleClose],
  )

  return {
    isOpen,
    surfaceRef,
    open,
    closeNow,
    triggerHandlers: { onMouseEnter: open, onMouseLeave: handlePointerLeave },
    surfaceHandlers: { onMouseEnter: open, onMouseLeave: handlePointerLeave, onBlur: handleSurfaceBlur },
  }
}
